/**
 * Error Handler Utilities
 */

import { 
  AppError, 
  AuthError, 
  NetworkError, 
  RateLimitError, 
  ApiError,
  ValidationError,
  DatabaseError,
  ServerError
} from './errors'
import type { ErrorCategory } from './types'

/**
 * Error handler utility class
 */
export class ErrorHandler {
  /**
   * Convert any error to AppError
   */
  static normalize(error: unknown): AppError {
    // If it's already an AppError, return it
    if (error instanceof AppError) {
      return error
    }

    // Handle Supabase errors
    if (this.isSupabaseError(error)) {
      return this.handleSupabaseError(error)
    }

    // Handle Prisma errors
    if (this.isPrismaError(error)) {
      return this.handlePrismaError(error)
    }

    // Handle fetch/network errors
    if (this.isNetworkError(error)) {
      return new NetworkError('Network request failed', {
        originalError: error
      })
    }

    // Handle standard errors
    if (error instanceof Error) {
      return new AppError(error.message, {
        originalError: error
      })
    }

    // Handle string errors
    if (typeof error === 'string') {
      return new AppError(error)
    }

    // Unknown error type
    return new AppError('An unknown error occurred', {
      originalError: error
    })
  }

  /**
   * Get error category
   */
  static getCategory(error: AppError): ErrorCategory {
    const codeMap: Record<string, ErrorCategory> = {
      'AUTH_ERROR': 'auth',
      'PERMISSION_DENIED': 'permission',
      'VALIDATION_ERROR': 'validation',
      'NOT_FOUND': 'not-found',
      'CONFLICT': 'conflict',
      'NETWORK_ERROR': 'network',
      'RATE_LIMIT_ERROR': 'rate-limit',
      'DATABASE_ERROR': 'database',
      'API_ERROR': 'api',
      'SERVER_ERROR': 'server'
    }

    return codeMap[error.code] || 'unknown'
  }

  /**
   * Get user-friendly error message
   */
  static getUserMessage(error: AppError): string {
    // For validation errors, provide more specific message if available
    if (error instanceof ValidationError && error.fields.length > 0) {
      const fieldMessages = error.fields
        .map(f => `${f.field}: ${f.message}`)
        .join(', ')
      return `Validation failed: ${fieldMessages}`
    }

    // For AUTH_ERROR, check if the error message is already user-friendly
    // and use it instead of the generic message
    if (error.code === 'AUTH_ERROR' && error.message) {
      // Check if the message is already user-friendly (not a technical error code)
      const technicalPatterns = [
        'AUTH_ERROR',
        'auth/',
        'SUPABASE',
        'Authentication failed'
      ]
      
      const isTechnicalMessage = technicalPatterns.some(pattern => 
        error.message.includes(pattern)
      )
      
      // If the message is user-friendly (like "Invalid email or password"), use it
      if (!isTechnicalMessage) {
        return error.message
      }
    }

    const userMessages: Record<string, string> = {
      'AUTH_ERROR': 'Authentication failed. Please check your credentials.',
      'PERMISSION_DENIED': 'You do not have permission to perform this action',
      'VALIDATION_ERROR': 'Please check your input and try again',
      'NOT_FOUND': 'The requested item could not be found',
      'CONFLICT': 'This item already exists',
      'NETWORK_ERROR': 'Connection error. Please check your internet and try again',
      'RATE_LIMIT_ERROR': 'Too many requests. Please wait a moment and try again',
      'DATABASE_ERROR': 'A database error occurred. Please try again later',
      'API_ERROR': 'An external service error occurred. Please try again later',
      'SERVER_ERROR': 'An unexpected error occurred. Please try again later'
    }

    return userMessages[error.code] || error.message
  }

  /**
   * Check if error is retriable
   */
  static isRetriable(error: AppError): boolean {
    const retriableCodes = [
      'NETWORK_ERROR',
      'RATE_LIMIT_ERROR',
      'SERVER_ERROR',
      'DATABASE_ERROR'
    ]

    return retriableCodes.includes(error.code) || 
           error.statusCode >= 500
  }

  /**
   * Log error appropriately
   */
  static log(error: AppError, logger?: any): void {
    const logData = {
      error: error.toJSON(),
      stack: error.stack
    }

    if (!logger) {
      if (error.isOperational) {
        console.warn('[ERROR]', error.message, logData)
      } else {
        console.error('[CRITICAL ERROR]', error.message, logData)
      }
      return
    }

    if (error.isOperational) {
      logger.warn(`[${error.code}] ${error.message}`, logData)
    } else {
      logger.error(`[CRITICAL ${error.code}] ${error.message}`, logData)
    }
  }

  /**
   * Format error for API response
   */
  static toApiResponse(error: AppError, includeStack = false) {
    const response: any = {
      error: {
        message: this.getUserMessage(error),
        code: error.code,
        statusCode: error.statusCode,
        timestamp: error.timestamp
      }
    }

    // Add validation fields if present
    if (error instanceof ValidationError && error.fields.length > 0) {
      response.error.fields = error.fields
    }

    // Add retry after for rate limit errors
    if (error instanceof RateLimitError && error.retryAfter) {
      response.error.retryAfter = error.retryAfter
    }

    // Include stack in development
    if (includeStack && process.env.NODE_ENV === 'development') {
      response.error.stack = error.stack
    }

    return response
  }

  /**
   * Check if error is Supabase error
   */
  private static isSupabaseError(error: any): boolean {
    return error && 
           typeof error === 'object' && 
           ('status' in error || 'code' in error) &&
           'message' in error
  }

  /**
   * Handle Supabase specific errors
   */
  private static handleSupabaseError(error: any): AppError {
    const status = error.status || error.statusCode
    // Coerce code to string — PostgREST occasionally returns non-string codes
    // (e.g. numeric SQLSTATE), and `.includes` on a number crashes with
    // "a.includes is not a function". Safari surfaces this the loudest.
    const code = typeof error.code === 'string' ? error.code : String(error.code ?? 'SUPABASE_ERROR')
    const message = error.message || 'Supabase error occurred'

    // Auth errors
    if (status === 401 || code.includes('auth')) {
      return new AuthError(message, {
        code: `SUPABASE_${code}`,
        originalError: error
      })
    }

    // Rate limit
    if (status === 429) {
      return new RateLimitError(message, undefined, {
        originalError: error
      })
    }

    // Not found
    if (status === 404) {
      return new AppError(message, {
        code: 'NOT_FOUND',
        statusCode: 404,
        originalError: error
      })
    }

    // Server errors
    if (status >= 500) {
      return new ServerError(message, {
        code: `SUPABASE_${code}`,
        originalError: error
      })
    }

    // Default API error
    return new ApiError(message, 'Supabase', undefined, {
      statusCode: status || 500,
      originalError: error
    })
  }

  /**
   * Check if error is Prisma error
   */
  private static isPrismaError(error: any): boolean {
    return error && 
           typeof error === 'object' && 
           'code' in error &&
           typeof error.code === 'string' &&
           error.code.startsWith('P')
  }

  /**
   * Handle Prisma specific errors
   */
  private static handlePrismaError(error: any): AppError {
    const code = error.code
    const message = error.message || 'Database error occurred'

    // Common Prisma error codes
    const errorMap: Record<string, () => AppError> = {
      'P2002': () => new ConflictError('A record with this value already exists', {
        originalError: error
      }),
      'P2025': () => new NotFoundError('Record', {
        originalError: error
      }),
      'P2003': () => new ValidationError('Foreign key constraint failed', [], {
        originalError: error
      }),
      'P2000': () => new ValidationError('Value too long for column', [], {
        originalError: error
      })
    }

    const errorFactory = errorMap[code]
    if (errorFactory) {
      return errorFactory()
    }

    // Default database error
    return new DatabaseError(message, {
      code: `PRISMA_${code}`,
      originalError: error
    })
  }

  /**
   * Check if error is network error
   */
  private static isNetworkError(error: any): boolean {
    if (error instanceof Error) {
      const networkKeywords = [
        'fetch',
        'network',
        'ECONNREFUSED',
        'ETIMEDOUT',
        'ENOTFOUND',
        'EHOSTUNREACH'
      ]
      
      return networkKeywords.some(keyword => 
        error.message.toLowerCase().includes(keyword.toLowerCase())
      )
    }
    return false
  }
}

import { ConflictError, NotFoundError } from './errors'